import Grid from '@material-ui/core/Grid/Grid';
import styles from './ContentContainer.module.css';
import agreementStyles from '../ClientAgreement.module.css';
import Button from '@material-ui/core/Button/Button';
import * as React from 'react';
import { useEffect, useState } from 'react';
import signatureImage from './benSignature.png';
import { formValueSelector, SubmitHandler } from 'redux-form';
import SignatureField from './SignatureField/SignatureField';
import CircularProgress from '@material-ui/core/CircularProgress';

type RequiredSignatures =
  | 'none'
  | 'advisorAndClient'
  | 'clientOnly'
  | 'initial'
  | 'clientAndInitial';

interface ContentContainerProps {
  children: Array<string | JSX.Element> | JSX.Element | string;
  name?: string;
  handleSubmit?: SubmitHandler<{}, {}, string>;
  saveOrSubmit?: (currentPage: number, submitted?: boolean) => void;
  existingSignature?: string;
  existingInitial?: string;
  isMobile?: boolean;
  onEdit?: () => void;
  finalReview?: boolean;
  review?: boolean;
  reviewHeader?: boolean;
  submittingAgreement: boolean;
  currentPage: number;
  requiredSignatures: RequiredSignatures;
  noScroll?: boolean;
}

function getSignatureText(requiredSignatures: RequiredSignatures) {
  const typeToText: { [key: string]: string | undefined } = {
    clientAndInitial: 'Signature & Initials',
    initial: 'Initials',
    advisorAndClient: 'Signatures',
  };
  return typeToText[requiredSignatures] || 'Signature';
}

const ContentContainer = ({
  children,
  name,
  handleSubmit,
  saveOrSubmit,
  existingSignature,
  existingInitial,
  isMobile,
  requiredSignatures,
  onEdit,
  finalReview,
  currentPage,
  submittingAgreement,
  review,
  reviewHeader,
  noScroll,
}: ContentContainerProps) => {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const hasScrolledToBottom = (targetElement: HTMLElement) => {
    if (scrolledToBottom) {
      return true; // Once read the whole thing, remain true
    }

    const roundedScrollValue = Math.ceil(Number(targetElement.scrollTop));
    const padding = 75;
    return targetElement.scrollHeight - roundedScrollValue < targetElement.clientHeight + padding;
  };

  useEffect(() => {
    const containerElement = document.getElementById(`content-container-${currentPage}`);
    setScrolledToBottom(hasScrolledToBottom(containerElement!));
  }, []);
  const content = (
    <>
      <Grid container alignItems="center" direction="column">
        <Grid item xs={12} sm={11} container>
          {reviewHeader && <h1 className={styles.reviewHeader}>{reviewHeader}</h1>}
        </Grid>
        <Grid
          id={`content-container-${currentPage}`}
          item
          xs={12}
          sm={11}
          style={isMobile ? { padding: '30px 3px' } : { padding: '30px' }}
          className={
            !finalReview && !review && !noScroll
              ? agreementStyles.contentContainer
              : agreementStyles.reviewContainer
          }
          onScroll={(evt) => setScrolledToBottom(hasScrolledToBottom(evt.target as HTMLElement))}>
          {children}
        </Grid>
      </Grid>
      {!review && requiredSignatures !== 'none' && (
        <>
          <Grid container justify="center">
            <Grid
              item
              container
              justify="center"
              xs={8}
              style={{ borderBottom: '1px solid #45aab6', padding: '20px 0px 10px 0px' }}>
              <b>{getSignatureText(requiredSignatures)}</b>
            </Grid>
          </Grid>
          <Grid container justify="center">
            <>
              {requiredSignatures === 'advisorAndClient' && (
                <SignatureField
                  id="ascend"
                  image={signatureImage}
                  enabled={false}
                  party="Ascend Debt Relief PLLC"
                  name="Benjamin Tejes, Co-Founder, Ascend"
                />
              )}
              {requiredSignatures !== 'initial' && (
                <SignatureField
                  id={`enrollee-${currentPage}`}
                  party="Enrollee"
                  existingSignature={existingSignature}
                  enabled
                  name={name!}
                />
              )}
              {(requiredSignatures === 'initial' || requiredSignatures === 'clientAndInitial') && (
                <SignatureField
                  id={`enrollee-initial-${currentPage}`}
                  party="Enrollee"
                  initial
                  existingSignature={existingInitial}
                  enabled
                  name={name!}
                />
              )}
            </>
          </Grid>
        </>
      )}

      {!review &&
        getButtonRow(saveOrSubmit, currentPage, scrolledToBottom, finalReview, submittingAgreement)}
    </>
  );
  return (
    <>
      {finalReview || review ? (
        // Don't want to nest <form>'s on review page
        <div>{content}</div>
      ) : (
        <form onSubmit={handleSubmit}>{content}</form>
      )}
    </>
  );
};

const getButtonRow = (
  saveOrSubmit?: (currentPage?: number, submitted?: boolean) => void,
  currentPage?: number,
  scrolledToBottom?: boolean,
  finalReview?: boolean,
  submittingAgreement?: boolean
) => {
  if (submittingAgreement) {
    return (
      <Grid container justify="center">
        <CircularProgress className={styles.loading} size={50} />
      </Grid>
    );
  }
  return (
    <>
      <Grid container justify="center" className={styles.buttonsContainer}>
        <Grid item xs={11} sm={8} md={6} container justify="space-around">
          <Grid item xs={6}>
            <Button
              color="secondary"
              variant="contained"
              onClick={() => saveOrSubmit!(currentPage)}>
              Save & Exit
            </Button>
          </Grid>
          <Grid item xs={6} container alignItems="center" direction="column">
            <Button
              color="primary"
              variant="contained"
              type={finalReview ? 'button' : 'submit'}
              onClick={finalReview ? () => saveOrSubmit!(currentPage, true) : undefined}
              disabled={!scrolledToBottom}>
              {finalReview ? 'Submit' : 'Next'}
            </Button>
            <span className={styles.warningText}>
              {!scrolledToBottom && 'Please read all text above'}
            </span>
          </Grid>
        </Grid>

        <Grid item xs={12} className={styles.contactText}>
          <i>
            Questions? Feel free to{' '}
            <a target="_blank" href="mailto:support@tryascend.com">
              contact us
            </a>
          </i>
        </Grid>
      </Grid>
    </>
  );
};

export default ContentContainer;

export const formSelector = formValueSelector('clientAgreement');
